import { useCallback, useEffect, useRef, useState } from "react";
import { Menu, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { chatApi } from "@/lib/data/requests/ai-chat";
import type {
  AiChatSearchHit,
  AiChatThreadStatus,
  ChatApi,
} from "@/lib/types/ai-chat";
import { useAiChatController } from "@/hooks/ai-chat/useAiChatController";
import { useAiChatThreads } from "@/hooks/ai-chat/useAiChatThreads";
import { useVisibleChatReadReceipt } from "@/hooks/ai-chat/useVisibleChatReadReceipt";
import { ChatComposer } from "./ChatComposer";
import { useOptionalUser } from "@/components/UserProvider";
import { ChatThreadList } from "./ChatThreadList";
import { ChatWelcome } from "./ChatWelcome";
import { ChatTimeline } from "./ChatTimeline";
import { ChatSearchPanel } from "./ChatSearchPanel";
import { ChatPendingApprovals } from "./ChatPendingApprovals";
import type { ChatFindTarget } from "@/hooks/ai-chat/useChatFindNavigation";
import type { SkillSummary } from "@/lib/data/schemas/skills";

interface ChatPageProps {
  api?: ChatApi;
  preview?: boolean;
  initialThreadId?: string;
  matterId?: string;
  matterTitle?: string;
  onThreadChange?: (threadId?: string) => void;
  onSettingsNavigate?: (page: "skills" | "automations") => void;
  previewSkills?: SkillSummary[];
}

function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export function ChatPage({
  api = chatApi,
  preview = false,
  initialThreadId,
  matterId,
  matterTitle,
  onThreadChange,
  onSettingsNavigate,
  previewSkills,
}: ChatPageProps) {
  const user = useOptionalUser();
  const queueScopeKey = user
    ? JSON.stringify([user.currentUser.firmId, user.currentUser.id])
    : preview
      ? "preview"
      : "";
  const [threadId, setThreadId] = useState(initialThreadId);
  const [composerGeneration, setComposerGeneration] = useState(0);
  const [status, setStatus] = useState<AiChatThreadStatus>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [findTarget, setFindTarget] = useState<ChatFindTarget>();
  const [visibleMatchCounts, setVisibleMatchCounts] = useState<
    Record<string, number>
  >({});
  const findRequestRef = useRef(0);
  const findQueryRef = useRef("");
  const selectedThreadRef = useRef(threadId);
  selectedThreadRef.current = threadId;
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [atBottom, setAtBottom] = useState(true);

  useEffect(() => {
    if (initialThreadId !== selectedThreadRef.current)
      setComposerGeneration((value) => value + 1);
    setThreadId(initialThreadId);
  }, [initialThreadId]);

  const selectThread = useCallback(
    (id?: string) => {
      setComposerGeneration((value) => value + 1);
      setThreadId(id);
      onThreadChange?.(id);
      setSidebarOpen(false);
      setSearchOpen(false);
      setFindQuery("");
      findQueryRef.current = "";
      setFindTarget(undefined);
      setVisibleMatchCounts({});
      findRequestRef.current += 1;
      setApprovalsOpen(false);
      setAtBottom(true);
    },
    [onThreadChange],
  );
  const clearDraft = useCallback(() => {}, []);
  const threads = useAiChatThreads({ api, status });
  const chat = useAiChatController({
    api,
    queueScopeKey,
    threadId,
    matterId,
    onThreadCreated: (id) => {
      // Promote the new conversation without discarding the next draft.
      setThreadId(id);
      onThreadChange?.(id);
      setSidebarOpen(false);
    },
  });
  const { cancelSearchReveal, revealSearchHit } = chat;
  const closeFind = useCallback(() => {
    cancelSearchReveal();
    setSearchOpen(false);
    setFindQuery("");
    findQueryRef.current = "";
    setFindTarget(undefined);
    setVisibleMatchCounts({});
    findRequestRef.current += 1;
  }, [cancelSearchReveal]);
  useEffect(() => {
    closeFind();
  }, [threadId, chat.awaitingAutomationCompletion, closeFind]);
  const changeFindQuery = useCallback(
    (query: string) => {
      cancelSearchReveal();
      const normalizedQuery = query.trim();
      findQueryRef.current = normalizedQuery;
      setFindQuery(normalizedQuery);
      setFindTarget(undefined);
      setVisibleMatchCounts({});
      findRequestRef.current += 1;
      if (query) setAtBottom(false);
    },
    [cancelSearchReveal],
  );
  const recordVisibleMatchCount = useCallback(
    (itemId: string, count: number, query: string) => {
      if (query !== findQueryRef.current) return;
      setVisibleMatchCounts((current) =>
        current[itemId] === count ? current : { ...current, [itemId]: count },
      );
    },
    [],
  );
  const selectFindMatch = useCallback(
    async (hit: AiChatSearchHit, query: string, occurrenceIndex: number) => {
      const requestId = ++findRequestRef.current;
      const currentThreadId = threadId;
      setAtBottom(false);
      await revealSearchHit(hit, query, occurrenceIndex);
      if (
        requestId !== findRequestRef.current ||
        currentThreadId !== selectedThreadRef.current
      )
        return;
      setFindTarget({ itemId: hit.itemId, requestId, query, occurrenceIndex });
    },
    [threadId, revealSearchHit],
  );
  const isRunning = Boolean(chat.activeRunId);
  const { hasNewer, items, revision, ackVisibleRevision } = chat;

  useVisibleChatReadReceipt({
    threadId,
    enabled: atBottom && !hasNewer && items.length > 0,
    revision,
    active: isRunning,
    acknowledge: ackVisibleRevision,
  });

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
        closeFind();
        setApprovalsOpen(false);
      }
      if (
        threadId &&
        !chat.isLoading &&
        !chat.awaitingAutomationCompletion &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "f"
      ) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [closeFind, threadId, chat.awaitingAutomationCompletion, chat.isLoading]);

  const newThread = useCallback(() => {
    selectThread();
  }, [selectThread]);
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);
  const { loadMore, refetch } = threads;
  const { jumpToLatest: jumpToLatestThread } = chat;
  const loadMoreThreads = useCallback(() => {
    void loadMore();
  }, [loadMore]);
  const retryThreads = useCallback(() => {
    void refetch();
  }, [refetch]);
  const jumpToLatest = useCallback(async () => {
    closeFind();
    return await jumpToLatestThread();
  }, [closeFind, jumpToLatestThread]);

  const threadList = (
    <ChatThreadList
      threads={threads.threads}
      selectedId={threadId}
      canAccessSkills={
        preview ||
        user?.hasAnyPagePermission(["skills.firm", "skills.personal"])
      }
      canAccessAutomations={
        preview ||
        user?.hasAnyPagePermission(["automations.firm", "automations.personal"])
      }
      onSettingsNavigate={onSettingsNavigate}
      status={status}
      onStatusChange={setStatus}
      onSelect={selectThread}
      onNew={newThread}
      onClose={closeSidebar}
      isLoading={threads.isLoading}
      isLoadingMore={threads.isFetchingNextPage}
      hasMore={threads.hasNextPage}
      onLoadMore={loadMoreThreads}
      error={errorMessage(threads.error)}
      onRetry={retryThreads}
    />
  );

  return (
    <div
      className="bg-background text-foreground flex h-full min-h-0 w-full overflow-hidden"
      data-testid="ai-chat-page"
    >
      <div className="border-border/70 hidden w-64 shrink-0 border-r md:block lg:w-68">
        {threadList}
      </div>
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Close thread navigation"
            onClick={() => {
              setSidebarOpen(false);
            }}
          />
          <div className="bg-background relative h-full w-72 shadow-xl">
            {threadList}
          </div>
        </div>
      )}
      <section
        aria-label="Conversation"
        className="relative flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <header className="border-border/60 flex h-16 shrink-0 items-center justify-between gap-3 border-b px-5 md:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              className="size-8 md:hidden"
              size="icon"
              variant="ghost"
              aria-label="Open threads"
              onClick={() => {
                setSidebarOpen(true);
              }}
            >
              <Menu />
            </Button>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-medium">
                {chat.thread?.title ||
                  (threadId ? "Conversation" : "LawWorks AI")}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {preview && (
              <span className="text-muted-foreground bg-muted mr-2 hidden rounded-md px-2 py-1 text-[10px] tracking-wide sm:inline">
                Local preview
              </span>
            )}
          </div>
        </header>
        {chat.thread &&
          ["failed", "failed_unread"].includes(chat.thread.status) && (
            <p
              role="status"
              className="text-destructive bg-destructive/5 mx-5 mt-3 shrink-0 rounded-lg px-4 py-3 text-sm"
            >
              The response stopped with an error. Try sending your message
              again.
            </p>
          )}
        {matterId && (
          <div className="border-border/50 text-muted-foreground flex shrink-0 items-center gap-2 border-b px-6 py-2 text-xs">
            <span>Context:</span>
            <a
              href={`/matters/${matterId}`}
              className="text-foreground truncate underline-offset-4 hover:underline"
            >
              {matterTitle || "Selected matter"}
            </a>
          </div>
        )}
        {chat.thread?.status === "pending_approval" && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-200/60 bg-amber-50/70 px-6 py-3 text-xs text-amber-900 dark:border-amber-400/15 dark:bg-amber-400/5 dark:text-amber-200">
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-4" />
              Your approval is needed to continue.
            </span>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setApprovalsOpen(true);
              }}
            >
              Review request
            </Button>
          </div>
        )}
        {chat.error && (
          <div
            role="alert"
            className="text-destructive bg-destructive/5 mx-5 mt-3 shrink-0 rounded-lg px-4 py-3 text-sm"
          >
            {errorMessage(chat.error)}
            <Button
              size="xs"
              variant="ghost"
              className="ml-2"
              onClick={() => {
                void chat.retryConnection();
              }}
            >
              Try again
            </Button>
          </div>
        )}
        {chat.streamStatus === "reconnecting" && (
          <div
            role="status"
            className="text-muted-foreground px-6 pt-2 text-xs"
          >
            Reconnecting to your conversation…
          </div>
        )}
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {chat.awaitingAutomationCompletion ? (
              <p
                role="status"
                className="text-muted-foreground m-auto max-w-md p-8 text-center text-sm"
              >
                The automation’s conversation will be available once it
                completes.
              </p>
            ) : (
              <ChatTimeline
                key={`timeline:${composerGeneration}`}
                threadId={threadId ?? `pending:${composerGeneration}`}
                emptyState={!threadId ? <ChatWelcome /> : undefined}
                queuedMessages={chat.queuedMessages}
                onRetryMessage={chat.retryMessage}
                onRemoveQueuedMessage={chat.removeQueuedMessage}
                onRevealItem={chat.revealTimelineItem}
                searchQuery={findQuery}
                searchTarget={findTarget}
                onVisibleMatchCount={recordVisibleMatchCount}
                items={chat.items}
                layoutItems={chat.layoutItems}
                onLoadWindow={chat.loadTimelineWindow}
                hasOlder={chat.hasOlder}
                isLoadingOlder={chat.isLoadingOlder}
                isFetchingTimeline={chat.isFetchingTimeline}
                isLoadingInitial={
                  chat.isLoading &&
                  !chat.items.length &&
                  !chat.queuedMessages.length
                }
                onLoadOlder={chat.loadOlder}
                onLoadNewer={chat.loadNewer}
                revision={chat.revision}
                bodyByItemId={chat.loadedBodies}
                loadingBodyIds={chat.pendingBodyIds}
                approvalDetails={chat.approvalDetails}
                decidingApprovalIds={chat.decidingReviewIds}
                onAnswerQuestion={chat.answerQuestion}
                onLoadBody={chat.loadItemBody}
                onLoadApprovalDetail={chat.loadApprovalDetail}
                onDecideApproval={chat.decideApproval}
                onAtBottomChange={setAtBottom}
                hasNewer={chat.hasNewer}
                isLoadingNewer={chat.isLoadingNewer}
                onJumpToLatest={jumpToLatest}
              />
            )}
            {chat.readOnly ? (
              <p className="text-muted-foreground mx-auto w-full max-w-[60rem] px-8 pb-5 text-sm">
                This conversation was started by an automation.
              </p>
            ) : (
              <ChatComposer
                key={`composer:${composerGeneration}`}
                onSend={chat.sendMessage}
                allowAttachments={!preview}
                previewSkills={previewSkills}
                onStop={chat.cancelRun}
                isRunning={isRunning}
                isSending={chat.isSending}
                canSteer={isRunning}
                disabled={
                  chat.isLoading &&
                  !chat.items.length &&
                  !chat.queuedMessages.length
                }
                onDraftUsed={clearDraft}
              />
            )}
          </div>
          {searchOpen &&
            threadId &&
            !chat.isLoading &&
            !chat.awaitingAutomationCompletion && (
              <div className="absolute top-2 right-3 z-30 max-w-[calc(100%-1.5rem)]">
                <ChatSearchPanel
                  api={api}
                  threadId={threadId}
                  onClose={closeFind}
                  onQueryChange={changeFindQuery}
                  onSelectMatch={selectFindMatch}
                  isNavigating={chat.isRevealingSearchHit}
                  visibleMatchCounts={visibleMatchCounts}
                />
              </div>
            )}
        </div>
        {preview && (
          <div className="text-muted-foreground bg-muted/40 border-border/60 flex h-7 shrink-0 items-center justify-center gap-1 border-t text-[10px]">
            Fictional data · Changes stay in this preview
          </div>
        )}
      </section>
      <ChatPendingApprovals
        api={api}
        runId={chat.activeRunId}
        open={approvalsOpen}
        onOpenChange={setApprovalsOpen}
        details={chat.approvalDetails}
        decidingIds={chat.decidingReviewIds}
        onLoadDetail={chat.loadApprovalDetail}
        onDecide={chat.decideApproval}
      />
    </div>
  );
}
